import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useCallbackRef } from "src/hooks/lifecycle";
import { compassColors } from "src/utils/styling";

const ErrorContext = createContext<{
  scrollToError: () => void;
  onError: (errorNode: HTMLElement) => void;
  onClear: (errorNode: HTMLElement) => void;
}>(null);

export function ErrorMessageProvider({ children }: { children: ReactNode }) {
  const [errorNodes, setErrorNodes] = useState<HTMLElement[]>([]);

  const onClear = useCallbackRef((error) => {
    setErrorNodes((currentNodes) => {
      return currentNodes.filter((node) => node !== error);
    });
  });
  const onError = useCallbackRef((add) => {
    setErrorNodes((currentNodes) => {
      if (!currentNodes.includes(add)) {
        return [...currentNodes, add];
      }
      return currentNodes;
    });
  });

  const scrollToError = useCallbackRef(() => {
    // Defer until the render has settled.
    setTimeout(() => {
      const rects = errorNodes
        .map((node) => node.getBoundingClientRect())
        .sort((aRect, bRect) => {
          return aRect.top - bRect.top;
        });

      const firstError = rects[0];
      if (firstError) {
        window.scrollBy({
          // Scroll up slightly to provide the user more context.
          top: firstError.top - 50,
          behavior: "smooth",
        });
      }
    }, 100);
  });

  const context = useMemo(
    () => ({ scrollToError, onClear, onError }),
    [scrollToError, onClear, onError]
  );

  return (
    <ErrorContext.Provider value={context}>{children}</ErrorContext.Provider>
  );
}

export function ErrorMessage({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  const { ref } = useErrorDisplay();

  return (
    <p
      ref={ref}
      css={{ color: compassColors.tarocco }}
      className={className}
      data-cy="error-message"
      role="alert"
    >
      {children}
    </p>
  );
}

export function useErrorManager() {
  return useContext(ErrorContext);
}

export function useErrorDisplay({ errored = true } = {}) {
  const lastNode = useRef<HTMLElement>(null);
  const { onError, onClear } = useErrorManager();

  useEffect(() => {
    if (lastNode.current) {
      if (errored) {
        onError(lastNode.current);
      } else {
        onClear(lastNode.current);
      }
    }
  }, [errored, onClear, onError]);

  function ref(el: HTMLElement) {
    if (el && errored) {
      onError(el);
    } else {
      onClear(lastNode.current);
    }

    lastNode.current = el;
  }

  return {
    ref: useCallbackRef(ref),
  };
}
